import { useState, useEffect } from 'react'
import { Settings, Save, Info, Power, AlertTriangle, Clock, History, Mail, ShieldCheck, Ban, Calendar, Timer } from 'lucide-react'
import { toast } from 'sonner'
import { limitsService, SystemLimits, AuditLog } from '../../services/limitsService'
import { requestTypeBlockService } from '../../services/requestTypeBlockService'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { REQUEST_TYPE_LABELS, RequestType } from '../../types'

// Interface para el estado local de bloqueos
interface BlockConfig {
  enabled: boolean
  blockDate: string
  blockTime: string
  allowedDays: number | null
  reason: string
}

export function LimitsPage() {
  const [limits, setLimits] = useState<SystemLimits | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isToggling, setIsToggling] = useState(false)
  const [isTogglingEmail, setIsTogglingEmail] = useState(false)
  const [showDisableConfirm, setShowDisableConfirm] = useState(false)
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [showAuditLogs, setShowAuditLogs] = useState(false)

  // Estado para bloqueos de tipos de solicitud
  const [typeBlocks, setTypeBlocks] = useState<Record<string, BlockConfig>>({})
  const [isSavingBlocks, setIsSavingBlocks] = useState(false)

  useEffect(() => {
    loadLimits()
    loadAuditLogs()
    loadTypeBlocks()
  }, [])

  const loadLimits = async () => {
    try {
      setIsLoading(true)
      const data = await limitsService.getLimits()
      setLimits(data)
    } catch (error) {
      console.error('Error loading limits:', error)
      toast.error('Error al cargar la configuración')
    } finally {
      setIsLoading(false)
    }
  }

  const loadAuditLogs = async () => {
    try {
      const logs = await limitsService.getAuditLogs(undefined, 20)
      setAuditLogs(logs.filter(l => l.action === 'REQUESTS_ENABLED' || l.action === 'REQUESTS_DISABLED'))
    } catch (error) {
      console.error('Error loading audit logs:', error)
    }
  }

  const loadTypeBlocks = async () => {
    try {
      const blocks = await requestTypeBlockService.getAll()
      // Convertir a formato local
      const blockConfigs: Record<string, BlockConfig> = {}

      // Inicializar todos los tipos con valores por defecto
      Object.keys(REQUEST_TYPE_LABELS).forEach((type) => {
        blockConfigs[type] = {
          enabled: false,
          blockDate: '',
          blockTime: '',
          allowedDays: null,
          reason: '',
        }
      })

      // Sobrescribir con los bloqueos existentes
      blocks.forEach((block) => {
        blockConfigs[block.requestType] = {
          enabled: block.isActive,
          blockDate: block.blockDate.split('T')[0], // Solo fecha sin hora
          blockTime: block.blockTime || '',
          allowedDays: block.allowedDays,
          reason: block.reason || '',
        }
      })

      setTypeBlocks(blockConfigs)
    } catch (error) {
      console.error('Error loading type blocks:', error)
    }
  }

  const handleSaveBlocks = async () => {
    try {
      setIsSavingBlocks(true)

      // Preparar los bloques para enviar
      const blocksToSave = Object.entries(typeBlocks)
        .filter(([_, config]) => config.enabled && config.blockDate)
        .map(([requestType, config]) => ({
          requestType: requestType as RequestType,
          blockDate: config.blockDate,
          blockTime: config.blockTime || undefined,
          allowedDays: config.allowedDays || undefined,
          reason: config.reason || undefined,
          isActive: true,
        }))

      // También incluir los que se desactivaron para eliminarlos
      const blocksToDelete = Object.entries(typeBlocks)
        .filter(([_, config]) => !config.enabled || !config.blockDate)
        .map(([requestType]) => ({
          requestType: requestType as RequestType,
          blockDate: new Date().toISOString(),
          delete: true,
        }))

      await requestTypeBlockService.bulkUpdate([...blocksToSave, ...blocksToDelete])
      toast.success('Bloqueos de tipos de solicitud guardados')
      // Recargar para asegurar sincronización con el servidor
      await loadTypeBlocks()
    } catch (error) {
      console.error('Error saving type blocks:', error)
      toast.error('Error al guardar los bloqueos')
    } finally {
      setIsSavingBlocks(false)
    }
  }

  const updateTypeBlock = (requestType: string, field: keyof BlockConfig, value: any) => {
    setTypeBlocks((prev) => ({
      ...prev,
      [requestType]: {
        ...prev[requestType],
        [field]: value,
      },
    }))
  }

  const handleSave = async () => {
    if (!limits) return

    try {
      setIsSaving(true)
      await limitsService.updateLimits(limits)
      toast.success('Configuración guardada correctamente')
    } catch (error) {
      console.error('Error saving limits:', error)
      toast.error('Error al guardar la configuración')
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleRequests = async (enable: boolean) => {
    if (!enable) {
      setShowDisableConfirm(true)
      return
    }
    await doToggle(true)
  }

  const doToggle = async (enable: boolean) => {
    try {
      setIsToggling(true)
      const { message } = await limitsService.toggleRequests(enable)
      setLimits(prev => prev ? { ...prev, requestsEnabled: enable } : null)
      toast.success(message)
      loadAuditLogs()
    } catch (error) {
      console.error('Error toggling requests:', error)
      toast.error('Error al cambiar el estado de solicitudes')
    } finally {
      setIsToggling(false)
      setShowDisableConfirm(false)
    }
  }

  const handleToggleEmailValidation = async () => {
    if (!limits) return

    const newValue = !limits.emailValidationRequired
    try {
      setIsTogglingEmail(true)
      await limitsService.updateLimits({ emailValidationRequired: newValue })
      setLimits(prev => prev ? { ...prev, emailValidationRequired: newValue } : null)
      toast.success(newValue
        ? 'Validación de email activada'
        : 'Validación de email desactivada')
    } catch (error) {
      console.error('Error toggling email validation:', error)
      toast.error('Error al cambiar la validación de email')
    } finally {
      setIsTogglingEmail(false)
    }
  }

  const updateLimit = (key: keyof SystemLimits, value: number) => {
    if (!limits) return
    setLimits({ ...limits, [key]: value })
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!limits) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No se pudo cargar la configuración</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Límites del Sistema
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configura los límites y restricciones del sistema de licencias
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          {isSaving ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>

      {/* Toggle de Solicitudes - DESTACADO */}
      <div className={`card p-6 border-2 ${limits?.requestsEnabled ? 'border-success-200 dark:border-success-800 bg-success-50/50 dark:bg-success-900/20' : 'border-error-200 dark:border-error-800 bg-error-50/50 dark:bg-error-900/20'}`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${limits?.requestsEnabled ? 'bg-success-100 dark:bg-success-800' : 'bg-error-100 dark:bg-error-800'}`}>
            <Power className={`h-6 w-6 ${limits?.requestsEnabled ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Creación de Solicitudes
              </h2>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${limits?.requestsEnabled ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                  {limits?.requestsEnabled ? 'Habilitadas' : 'Deshabilitadas'}
                </span>
                <button
                  onClick={() => handleToggleRequests(!limits?.requestsEnabled)}
                  disabled={isToggling}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    limits?.requestsEnabled
                      ? 'bg-success-600 focus:ring-success-500'
                      : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-500'
                  } ${isToggling ? 'opacity-50 cursor-wait' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      limits?.requestsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {limits?.requestsEnabled
                ? 'Los empleados pueden crear nuevas solicitudes de licencias normalmente.'
                : 'Los empleados NO pueden crear solicitudes. Solo los administradores pueden hacerlo.'}
            </p>
            {!limits?.requestsEnabled && (
              <div className="bg-error-100 dark:bg-error-900/30 border border-error-200 dark:border-error-700 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-error-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error-700 dark:text-error-300">
                  <strong>Atención:</strong> Mientras las solicitudes estén deshabilitadas, los empleados verán un mensaje indicando que no pueden crear solicitudes y deberán contactar con RRHH.
                </p>
              </div>
            )}
            {auditLogs.length > 0 && (
              <button
                onClick={() => setShowAuditLogs(!showAuditLogs)}
                className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
              >
                <History className="h-4 w-4" />
                {showAuditLogs ? 'Ocultar historial' : 'Ver historial de cambios'}
              </button>
            )}
            {showAuditLogs && auditLogs.length > 0 && (
              <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Historial de auditoría</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-start gap-2 text-sm">
                      <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${log.action === 'REQUESTS_ENABLED' ? 'bg-success-500' : 'bg-error-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium">{log.userName}</span>
                          {' '}
                          {log.action === 'REQUESTS_ENABLED' ? 'habilitó' : 'deshabilitó'} las solicitudes
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(log.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toggle de Validación de Email */}
      <div className={`card p-6 border-2 ${limits?.emailValidationRequired ? 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/20'}`}>
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${limits?.emailValidationRequired ? 'bg-blue-100 dark:bg-blue-800' : 'bg-gray-100 dark:bg-gray-700'}`}>
            <Mail className={`h-6 w-6 ${limits?.emailValidationRequired ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Validación de Email al Primer Acceso
              </h2>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${limits?.emailValidationRequired ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {limits?.emailValidationRequired ? 'Activada' : 'Desactivada'}
                </span>
                <button
                  onClick={handleToggleEmailValidation}
                  disabled={isTogglingEmail}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    limits?.emailValidationRequired
                      ? 'bg-blue-600 focus:ring-blue-500'
                      : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-500'
                  } ${isTogglingEmail ? 'opacity-50 cursor-wait' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      limits?.emailValidationRequired ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {limits?.emailValidationRequired
                ? 'Los empleados que se logueen por primera vez deberán introducir un email y validarlo con un código antes de poder acceder al sistema.'
                : 'Los empleados pueden acceder directamente después de cambiar su contraseña genérica, sin validación de email.'}
            </p>
            {limits?.emailValidationRequired && (
              <div className="bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-3 flex items-start gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p><strong>Flujo de primer acceso:</strong></p>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Empleado introduce DNI y contraseña genérica</li>
                    <li>Se le pide introducir un email y nueva contraseña</li>
                    <li>Recibe un código de 6 dígitos en el email</li>
                    <li>Debe introducir el código para completar el registro</li>
                  </ol>
                </div>
              </div>
            )}
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 italic">
              💡 Desactiva esta opción para hacer pruebas sin necesidad de verificar emails.
            </p>
          </div>
        </div>
      </div>

      {/* Bloqueo de Tipos de Solicitud por Fecha */}
      <div className="card p-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="p-3 bg-amber-100 dark:bg-amber-800 rounded-lg">
            <Ban className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Bloqueo de Tipos de Solicitud
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Configura qué tipos de licencias se bloquean a partir de una fecha. Los empleados no verán estos tipos en el selector después de la fecha indicada.
                </p>
              </div>
              <button
                onClick={handleSaveBlocks}
                disabled={isSavingBlocks}
                className="btn btn-primary flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSavingBlocks ? 'Guardando...' : 'Guardar bloqueos'}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {Object.entries(REQUEST_TYPE_LABELS).map(([requestType, label]) => {
            const config = typeBlocks[requestType] || { enabled: false, blockDate: '', allowedDays: null, reason: '' }
            const isBlocked = config.enabled && config.blockDate && new Date(config.blockDate) <= new Date()

            return (
              <div
                key={requestType}
                className={`p-4 rounded-xl border-2 transition-all ${
                  config.enabled
                    ? isBlocked
                      ? 'border-error-200 dark:border-error-800 bg-error-50/50 dark:bg-error-900/20'
                      : 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/20'
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Toggle */}
                  <div className="pt-1">
                    <button
                      onClick={() => updateTypeBlock(requestType, 'enabled', !config.enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        config.enabled
                          ? 'bg-amber-600 focus:ring-amber-500'
                          : 'bg-gray-300 dark:bg-gray-600 focus:ring-gray-500'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          config.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {label}
                      </h3>
                      {isBlocked && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-error-100 text-error-700 dark:bg-error-900/50 dark:text-error-400 rounded-full">
                          Bloqueado
                        </span>
                      )}
                      {config.enabled && !isBlocked && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 rounded-full">
                          Pendiente de bloqueo
                        </span>
                      )}
                    </div>

                    {config.enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-3">
                        {/* Fecha de bloqueo */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            <Calendar className="inline h-3 w-3 mr-1" />
                            Fecha de bloqueo
                          </label>
                          <input
                            type="date"
                            value={config.blockDate}
                            onChange={(e) => updateTypeBlock(requestType, 'blockDate', e.target.value)}
                            className="input text-sm w-full"
                          />
                        </div>

                        {/* Hora de bloqueo */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            <Timer className="inline h-3 w-3 mr-1" />
                            Hora (opcional)
                          </label>
                          <input
                            type="time"
                            value={config.blockTime}
                            onChange={(e) => updateTypeBlock(requestType, 'blockTime', e.target.value)}
                            className="input text-sm w-full"
                            placeholder="00:00"
                          />
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            Por defecto: 00:00
                          </p>
                        </div>

                        {/* Días permitidos (solo para LSS) */}
                        {requestType === 'LICENCIA_SIN_SUELDO' && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Permitir solo (días)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="30"
                              value={config.allowedDays || ''}
                              onChange={(e) => updateTypeBlock(requestType, 'allowedDays', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="Ej: 2"
                              className="input text-sm w-full"
                            />
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              Solo se permitirán solicitudes de estos días
                            </p>
                          </div>
                        )}

                        {/* Motivo */}
                        <div className={requestType === 'LICENCIA_SIN_SUELDO' ? '' : 'sm:col-span-2'}>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Motivo (opcional)
                          </label>
                          <input
                            type="text"
                            value={config.reason}
                            onChange={(e) => updateTypeBlock(requestType, 'reason', e.target.value)}
                            placeholder="Ej: Fin del plazo de solicitud"
                            className="input text-sm w-full"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Info */}
        <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <strong>Ejemplo:</strong> Para bloquear Premio Permanencia y Licencia Sin Sueldo (excepto 2 días) a partir del 1 de marzo a las 14:00, activa ambos tipos, pon fecha 01/03/2026, hora 14:00, y en LSS indica "2" en "Permitir solo (días)".
            </p>
          </div>
        </div>
      </div>

      {/* Cancellation Limit - Main Feature */}
      <div className="card p-6 border-2 border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/20">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-100 dark:bg-primary-800 rounded-lg">
            <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Límite de Cancelación
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Días mínimos de antelación que debe tener un empleado para poder cancelar un permiso aprobado.
              Los administradores pueden cancelar sin esta restricción.
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="90"
                  value={limits.cancellationMinDays}
                  onChange={(e) => updateLimit('cancellationMinDays', parseInt(e.target.value) || 0)}
                  className="input w-24 text-center text-lg font-semibold"
                />
                <span className="text-gray-600 dark:text-gray-400">días</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Info className="h-4 w-4" />
                <span>Si el permiso empieza en menos de {limits.cancellationMinDays} días, el empleado no podrá cancelarlo</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Other Limits */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Otros Límites
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Min Advance Notice */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Antelación mínima para solicitar (días)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Días de antelación mínimos para crear una solicitud
            </p>
            <input
              type="number"
              min="0"
              value={limits.minAdvanceNotice}
              onChange={(e) => updateLimit('minAdvanceNotice', parseInt(e.target.value) || 0)}
              className="input w-full"
            />
          </div>

          {/* Extension Notice */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Antelación para prórrogas (días)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Días de antelación para solicitar prórroga
            </p>
            <input
              type="number"
              min="0"
              value={limits.extensionNotice}
              onChange={(e) => updateLimit('extensionNotice', parseInt(e.target.value) || 0)}
              className="input w-full"
            />
          </div>

          {/* Max Concurrent Percent */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Máximo concurrente (% plantilla)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Porcentaje máximo de la plantilla fija en excedencia simultánea
            </p>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={limits.maxConcurrentPercent}
              onChange={(e) => updateLimit('maxConcurrentPercent', parseFloat(e.target.value) || 0)}
              className="input w-full"
            />
          </div>

          {/* Min Concurrent Workers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Mínimo de trabajadores concurrentes
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Mínimo garantizado aunque el % sea menor
            </p>
            <input
              type="number"
              min="0"
              value={limits.minConcurrentWorkers}
              onChange={(e) => updateLimit('minConcurrentWorkers', parseInt(e.target.value) || 0)}
              className="input w-full"
            />
          </div>

          {/* Request Deadline */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fecha límite de solicitudes anuales
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Fecha antes de la cual deben presentarse las solicitudes de 7 días de LSS y Premio Permanencia
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 dark:text-gray-400">Día</span>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={limits.requestDeadlineDay}
                  onChange={(e) => updateLimit('requestDeadlineDay', parseInt(e.target.value) || 1)}
                  className="input w-20 text-center"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600 dark:text-gray-400">de</span>
                <select
                  value={limits.requestDeadlineMonth}
                  onChange={(e) => updateLimit('requestDeadlineMonth', parseInt(e.target.value))}
                  className="input"
                >
                  <option value={1}>Enero</option>
                  <option value={2}>Febrero</option>
                  <option value={3}>Marzo</option>
                  <option value={4}>Abril</option>
                  <option value={5}>Mayo</option>
                  <option value={6}>Junio</option>
                  <option value={7}>Julio</option>
                  <option value={8}>Agosto</option>
                  <option value={9}>Septiembre</option>
                  <option value={10}>Octubre</option>
                  <option value={11}>Noviembre</option>
                  <option value={12}>Diciembre</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Nota importante</p>
            <p>
              Estos límites aplican globalmente al sistema. Las reglas específicas por tipo de solicitud
              se configuran en la sección de <strong>Reglas</strong>. Los cambios aquí afectan a todas
              las nuevas solicitudes y cancelaciones.
            </p>
          </div>
        </div>
      </div>

      {/* Confirm Disable Dialog */}
      <ConfirmDialog
        isOpen={showDisableConfirm}
        onClose={() => setShowDisableConfirm(false)}
        onConfirm={() => doToggle(false)}
        title="Deshabilitar creación de solicitudes"
        message="Al deshabilitar las solicitudes, los empleados NO podrán crear nuevas solicitudes de licencias. Solo los administradores podrán hacerlo. Este cambio quedará registrado en el historial de auditoría. ¿Estás seguro de continuar?"
        confirmText="Sí, deshabilitar"
        cancelText="Cancelar"
        variant="danger"
      />
    </div>
  )
}
